import React, { useEffect, useState, useMemo } from 'react';
import api from '../api/api';
import TaskForm from '../components/tasks/TaskForm';
import TaskTimer from '../components/tasks/TaskTimer';
import TaskReport from '../components/tasks/TaskReport';
import { useAuth } from '../contexts/AuthContext';
import { Icon, Button } from '../components/ui';
import { showConfirm } from '../utils/confirmUtils';
import { toastSuccess, toastError, toastInfo } from '../utils/toasts';
import ExportModal from '../components/ExportModal';

const CATEGORY_OPTIONS = [
  { value: '', label: 'Toutes catégories' },
  { value: 'accounting', label: 'Comptabilité' },
  { value: 'fiscal', label: 'Fiscal' },
  { value: 'social', label: 'Social' },
  { value: 'legal', label: 'Juridique' },
  { value: 'other', label: 'Autre' },
];

const STATUS_LABELS = {
  pending: 'En attente',
  in_progress: 'En cours',
  completed: 'Terminé',
  overdue: 'En retard',
};

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [assignees, setAssignees] = useState([]); // admin + collaborateurs (sans clients)
  const [loading, setLoading] = useState(true);

  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [activeTab, setActiveTab] = useState('my-tasks');
  const [viewMode, setViewMode] = useState('kanban');

  const [filters, setFilters] = useState({
    status: '',
    clientId: '',
    priority: '',
    category: '',
    search: '',
    assignedTo: '',
  });

  const { currentUser } = useAuth();
  const currentUserId = (currentUser?._id || currentUser?.id || '').toString();
  const isAdmin =
    currentUser?.role === 'admin' || currentUser?.permissions?.globalSuperAdmin;

  const isArchivedTab = activeTab === 'archived';

  const [showExportModal, setShowExportModal] = useState(false);

  const taskColumns = [
    { key: 'title', label: 'Titre', accessor: (t) => t?.title || '' },
    { key: 'client', label: 'Client', accessor: (t) => t?.clientId?.companyName || t?.clientId?.name || 'N/A' },
    { key: 'assignee', label: 'Assigné à', accessor: (t) => t?.assignedTo?.name || t?.assignedTo?.email || 'Non assigné' },
    { key: 'category', label: 'Catégorie', accessor: (t) => t?.category || '' },
    { key: 'priority', label: 'Priorité', accessor: (t) => t?.priority || 'medium' },
    { key: 'status', label: 'Statut', accessor: (t) => STATUS_LABELS[t?.status] || t?.status || '' },
    { key: 'dueDate', label: 'Échéance', accessor: (t) => t?.dueDate ? new Date(t.dueDate).toLocaleDateString('fr-FR') : '' },
  ];

  // --- CHARGEMENT DONNÉES ---
  const loadData = async () => {
    setLoading(true);
    try {
      // Charger référentiels une seule fois
      if (clients.length === 0 || assignees.length === 0) {
        const [clRes, usRes] = await Promise.all([
          api.get('/clients'),
          api.get('/users'), // ✅ on charge tout puis on filtre côté front
        ]);

        setClients(clRes.data || []);

        const users = usRes.data || [];
        const filteredAssignees = users.filter((u) => {
          if (!u) return false;
          const role = u.role;
          const isSuperAdmin = !!u.permissions?.globalSuperAdmin;
          return role === 'admin' || role === 'collaborator' || role === 'collaborateur' || isSuperAdmin;
        });

        // tri stable (nom)
        filteredAssignees.sort((a, b) =>
          String(a.name || a.email || '').localeCompare(String(b.name || b.email || ''), 'fr')
        );

        setAssignees(filteredAssignees);
      }

      let endpoint = '/tasks';
      if (activeTab === 'archived') endpoint = '/tasks/archived-tasks';

      const res = await api.get(endpoint);
      let loadedTasks = res.data || [];

      // Onglet rapports : on veut tout
      if (activeTab === 'reports') {
        const [archivedRes, activeRes] = await Promise.all([
          api.get('/tasks/archived-tasks'),
          api.get('/tasks'),
        ]);
        loadedTasks = [...(activeRes.data || []), ...(archivedRes.data || [])];
      }

      setTasks(loadedTasks);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // --- ACTIONS ---
  const handleStatusChange = async (taskId, newStatus) => {
    try {
      setTasks((prev) =>
        prev.map((t) => (t._id === taskId ? { ...t, status: newStatus } : t))
      );
      await api.put(`/tasks/${taskId}`, { status: newStatus });
    } catch (e) {
      console.error(e);
      loadData();
    }
  };

  const handleAdminValidateAndArchive = async (task) => {
    if (!await showConfirm('Valider et archiver ?')) return;
    try {
      await api.post(`/tasks/${task._id}/validate-and-archive`, {
        reason: 'admin_validation',
      });
      loadData();
    } catch (e) {
      alert("Erreur validation");
    }
  };

  const handleArchive = async (task) => {
    if (!await showConfirm('Archiver ?')) return;
    try {
      await api.put(`/tasks/${task._id}`, {
        isArchived: true,
        archiveDate: new Date(),
      });
      loadData();
    } catch (e) {
      alert("Erreur archivage");
    }
  };

  // ✅ SUPPRESSION : active => /tasks/:id ; archivée => /tasks/archived-tasks/:id
  const handleDeleteTask = async (task) => {
    if (!isAdmin) return;

    const ok = window.confirm(
      `Supprimer définitivement la tâche "${task?.title || ''}" ?\n\n⚠️ Action irréversible`
    );
    if (!ok) return;

    try {
      if (isArchivedTab || task?.archivedAt) {
        await api.delete(`/tasks/archived-tasks/${task._id}`);
      } else {
        await api.delete(`/tasks/${task._id}`);
      }
      loadData();
    } catch (e) {
      console.error('Erreur suppression tâche:', e);
      alert('❌ Erreur lors de la suppression');
    }
  };

  // --- HELPERS ---
  const getClientName = (t) => t.clientId?.companyName || t.clientId?.name || 'N/A';
  const getAssigneeName = (t) => t.assignedTo?.name || t.assignedTo?.email || 'Non assigné';

  const canShowTimer = (t) => !isArchivedTab && !t.isArchived && t.status !== 'completed';

  // --- FILTRAGE ---
  const filteredTasks = useMemo(() => {
    if (activeTab === 'reports') return tasks;

    return tasks.filter((t) => {
      // 1) Filtre Onglets
      if (activeTab === 'my-tasks') {
        const assignedId = (t.assignedTo?._id || t.assignedTo || '').toString();
        if (assignedId !== currentUserId) return false;
      }

      // 2) Filtres communs
      if (!isArchivedTab) {
        if (filters.status && t.status !== filters.status) return false;
      }

      if (filters.priority && t.priority !== filters.priority) return false;
      if (filters.category && t.category !== filters.category) return false;

      if (filters.clientId) {
        const cId = (t.clientId?._id || t.clientId || '').toString();
        if (cId !== String(filters.clientId)) return false;
      }

      // 3) Filtre collaborateur (onglet équipe)
      if (filters.assignedTo) {
        const uId = (t.assignedTo?._id || t.assignedTo || '').toString();
        if (uId !== String(filters.assignedTo)) return false;
      }

      // 4) Recherche (titre + client)
      if (filters.search) {
        const q = filters.search.toLowerCase().trim();
        const matchTitle = (t.title || '').toLowerCase().includes(q);
        const matchClient = (t.clientId?.companyName || t.clientId?.name || '').toLowerCase().includes(q);
        if (!matchTitle && !matchClient) return false;
      }

      return true;
    });
  }, [tasks, activeTab, filters, currentUserId, isArchivedTab]);

  const kanbanGroups = useMemo(
    () => ({
      pending: filteredTasks.filter((t) => t.status === 'pending'),
      in_progress: filteredTasks.filter((t) => t.status === 'in_progress'),
      completed: filteredTasks.filter((t) => t.status === 'completed'),
      overdue: filteredTasks.filter((t) => t.status === 'overdue'),
    }),
    [filteredTasks]
  );

  const resetFilters = () => {
    setFilters({
      status: '',
      clientId: '',
      priority: '',
      category: '',
      search: '',
      assignedTo: '',
    });
  };

  if (loading && tasks.length === 0) return <div className="p-10 text-center">Chargement...</div>;

  return (
    <div className="w-full px-4 py-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">
          {activeTab === 'reports' ? 'Rapports & Analyses' : 'Gestion des Tâches'}
        </h1>

        {activeTab !== 'reports' && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowExportModal(true)}
              disabled={filteredTasks.length === 0}
              className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 shadow flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="IconFileExport" /> Export
            </button>
            <div className="flex bg-white rounded-lg shadow border border-gray-200 p-1">
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-1 rounded ${viewMode === 'kanban' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500'}`}
                title="Vue Kanban"
              >
                <Icon name="IconColumns" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1 rounded ${viewMode === 'table' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500'}`}
                title="Vue Tableau"
              >
                <Icon name="IconTable" />
              </button>
            </div>

            {/* ✅ Pas de création dans Archives */}
            {!isArchivedTab && (
              <button
                onClick={() => {
                  setEditingTask(null);
                  setShowTaskForm(true);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow flex items-center gap-2"
              >
                <Icon name="IconPlus" /> Créer
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 mb-6 overflow-x-auto">
        {['my-tasks', 'team', 'archived', 'reports'].map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setFilters((prev) => ({
                ...prev,
                assignedTo: '',
                status: tab === 'archived' ? '' : prev.status,
              }));
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'my-tasks'
              ? 'Mes Tâches'
              : tab === 'team'
              ? 'Équipe'
              : tab === 'archived'
              ? 'Archives'
              : 'Rapports'}
          </button>
        ))}
      </div>

      {/* CONTENU */}
      {activeTab === 'reports' ? (
        <TaskReport tasks={tasks} clients={clients} collaborators={assignees} />
      ) : (
        <>
          {/* BARRE DE FILTRES */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[220px]">
              <input
                type="text"
                placeholder="Recherche (nom tâche / client)..."
                className="w-full border rounded px-3 py-2 text-sm"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>

            <select
              className="border rounded px-3 py-2 text-sm w-44"
              value={filters.clientId}
              onChange={(e) => setFilters({ ...filters, clientId: e.target.value })}
            >
              <option value="">Tous les clients</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.companyName}
                </option>
              ))}
            </select>

            <select
              className="border rounded px-3 py-2 text-sm w-44"
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {activeTab === 'team' && (
              <select
                className="border rounded px-3 py-2 text-sm w-44"
                value={filters.assignedTo}
                onChange={(e) => setFilters({ ...filters, assignedTo: e.target.value })}
              >
                <option value="">Tous (admin + collab.)</option>
                {assignees.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.name || u.email}
                  </option>
                ))}
              </select>
            )}

            {/* ✅ Statut: pas affiché dans Archives */}
            {!isArchivedTab && (
              <select
                className="border rounded px-3 py-2 text-sm w-40"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Tous statuts</option>
                <option value="pending">En attente</option>
                <option value="in_progress">En cours</option>
                <option value="completed">Terminé</option>
                <option value="overdue">En retard</option>
              </select>
            )}

            <button onClick={resetFilters} className="text-red-500 text-sm hover:underline px-2">
              Réinitialiser
            </button>
          </div>

          {/* VUE KANBAN */}
          {viewMode === 'kanban' && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 overflow-x-auto">
              {Object.entries(kanbanGroups).map(([status, groupTasks]) => (
                <div
                  key={status}
                  className="bg-gray-100 rounded-lg p-3 min-w-[260px] flex flex-col h-[calc(100vh-250px)]"
                >
                  <h3 className="font-bold text-gray-700 uppercase text-xs mb-3 flex justify-between">
                    {STATUS_LABELS[status] || status}{' '}
                    <span className="bg-gray-200 px-2 rounded-full">{groupTasks.length}</span>
                  </h3>

                  <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar pr-0">
                    {groupTasks.map((task) => {
                      const localIsArchived = isArchivedTab || !!task.isArchived || !!task.archivedAt;

                      const showValidate = isAdmin && task.status === 'completed' && !localIsArchived;
                      const showArchive = !isAdmin && task.status === 'completed' && !localIsArchived;

                      return (
                        <div
                          key={task._id}
                          className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition group relative w-full"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                                task.priority === 'urgent'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-blue-50 text-blue-600'
                              }`}
                            >
                              {task.priority || 'medium'}
                            </span>

                            {/* ✅ ARCHIVES : pas Modifier/Archiver, seulement Supprimer (admin) */}
                            <div className="flex items-center gap-2">
                              {!localIsArchived && (
                                <button
                                  onClick={() => {
                                    setEditingTask(task);
                                    setShowTaskForm(true);
                                  }}
                                  className="text-gray-400 hover:text-indigo-600"
                                  title="Modifier"
                                >
                                  <Icon name="IconPencil" />
                                </button>
                              )}

                              {isAdmin && (
                                <button
                                  onClick={() => handleDeleteTask(task)}
                                  className="text-gray-400 hover:text-red-600"
                                  title="Supprimer définitivement"
                                >
                                  <Icon name="IconTrash" />
                                </button>
                              )}
                            </div>
                          </div>

                          <h4 className="font-semibold text-gray-800 text-sm mb-1">{task.title}</h4>

                          <div className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                            <span className="inline-flex items-center gap-1">
                              <Icon name="IconBuilding" /> {getClientName(task)}
                            </span>
                            <span className="text-gray-300">•</span>
                            <span className="inline-flex items-center gap-1">
                              <Icon name="IconTag" /> {task.category || 'other'}
                            </span>
                            {localIsArchived && (
                              <>
                                <span className="text-gray-300">•</span>
                                <span className="text-[10px] font-bold text-gray-500 uppercase">
                                  Archivée
                                </span>
                              </>
                            )}
                          </div>

                          {canShowTimer(task) && (
                            <div className="mb-3 bg-gray-50 p-2 rounded border border-gray-100">
                              <TaskTimer taskId={task._id} onTimerUpdate={loadData} />
                            </div>
                          )}

                          {/* ✅ Archives : statut non modifiable */}
                          {!localIsArchived ? (
                            <div className="mb-3">
                              <select
                                value={task.status}
                                onChange={(e) => handleStatusChange(task._id, e.target.value)}
                                className="w-full text-xs border border-gray-200 rounded px-2 py-1 bg-gray-50 hover:bg-white transition cursor-pointer"
                              >
                                <option value="pending">En attente</option>
                                <option value="in_progress">En cours</option>
                                <option value="completed">Terminé</option>
                                <option value="overdue">En retard</option>
                              </select>
                            </div>
                          ) : (
                            <div className="mb-3 text-xs text-gray-600">
                              Statut : <span className="font-semibold">{STATUS_LABELS[task.status] || task.status}</span>
                            </div>
                          )}

                          <div className="flex justify-between items-center border-t pt-2 mt-2">
                            <div
                              className="flex items-center gap-2 text-xs text-gray-500"
                              title={getAssigneeName(task)}
                            >
                              <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">
                                {getAssigneeName(task).charAt(0)}
                              </div>
                              <span className="truncate max-w-[140px]">{getAssigneeName(task)}</span>
                            </div>

                            {/* ✅ Archives : aucune action Valider/Archiver */}
                            {!localIsArchived && showValidate && (
                              <button
                                onClick={() => handleAdminValidateAndArchive(task)}
                                className="text-xs bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700 shadow-sm"
                                title="Valider + Archiver"
                              >
                                <Icon name="IconChecks" className="mr-1" /> Valider
                              </button>
                            )}

                            {!localIsArchived && showArchive && (
                              <button
                                onClick={() => handleArchive(task)}
                                className="text-xs bg-gray-700 text-white px-2 py-1 rounded hover:bg-gray-800 shadow-sm"
                                title="Archiver"
                              >
                                <Icon name="IconArchive" className="mr-1" /> Archiver
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* VUE TABLEAU */}
          {viewMode === 'table' && (
            <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 text-gray-700">
                    <tr>
                      <th className="text-left px-4 py-3 whitespace-nowrap">Tâche</th>
                      <th className="text-left px-4 py-3 whitespace-nowrap">Client</th>
                      <th className="text-left px-4 py-3 whitespace-nowrap">Assigné</th>
                      <th className="text-left px-4 py-3 whitespace-nowrap">Catégorie</th>
                      <th className="text-left px-4 py-3 whitespace-nowrap">Priorité</th>
                      <th className="text-left px-4 py-3 whitespace-nowrap">Échéance</th>
                      <th className="text-left px-4 py-3 whitespace-nowrap">Statut</th>
                      <th className="text-right px-4 py-3 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredTasks.map((t) => {
                      const localIsArchived = isArchivedTab || !!t.isArchived || !!t.archivedAt;

                      const showValidate = isAdmin && t.status === 'completed' && !localIsArchived;
                      const showArchive = !isAdmin && t.status === 'completed' && !localIsArchived;

                      return (
                        <tr key={t._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 w-[38%]">
                            <div className="font-semibold text-gray-800">{t.title}</div>
                            {localIsArchived && (
                              <div className="text-[11px] font-bold text-gray-500 uppercase mt-1">Archivée</div>
                            )}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">{getClientName(t)}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{getAssigneeName(t)}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{t.category || 'other'}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{t.priority || 'medium'}</td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            {t.dueDate ? new Date(t.dueDate).toLocaleDateString('fr-FR') : ''}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            {!localIsArchived ? (
                              <select
                                value={t.status}
                                onChange={(e) => handleStatusChange(t._id, e.target.value)}
                                className="text-xs border border-gray-200 rounded px-2 py-1 bg-gray-50 hover:bg-white transition cursor-pointer"
                              >
                                <option value="pending">En attente</option>
                                <option value="in_progress">En cours</option>
                                <option value="completed">Terminé</option>
                                <option value="overdue">En retard</option>
                              </select>
                            ) : (
                              <span className="text-xs font-semibold text-gray-700">
                                {STATUS_LABELS[t.status] || t.status}
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <div className="inline-flex items-center gap-2">
                              {/* ✅ Archives : pas Modifier */}
                              {!localIsArchived && (
                                <button
                                  onClick={() => {
                                    setEditingTask(t);
                                    setShowTaskForm(true);
                                  }}
                                  className="px-2 py-1 rounded border border-gray-200 hover:bg-white text-gray-700"
                                  title="Modifier"
                                >
                                  <Icon name="IconPencil" />
                                </button>
                              )}

                              {!localIsArchived && showValidate && (
                                <button
                                  onClick={() => handleAdminValidateAndArchive(t)}
                                  className="px-3 py-1 rounded bg-purple-600 hover:bg-purple-700 text-white text-xs"
                                  title="Valider + Archiver"
                                >
                                  Valider
                                </button>
                              )}

                              {!localIsArchived && showArchive && (
                                <button
                                  onClick={() => handleArchive(t)}
                                  className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-800 text-white text-xs"
                                  title="Archiver"
                                >
                                  Archiver
                                </button>
                              )}

                              {/* ✅ Archives : uniquement suppression admin */}
                              {isAdmin && (
                                <button
                                  onClick={() => handleDeleteTask(t)}
                                  className="px-2 py-1 rounded border border-red-200 hover:bg-red-50 text-red-700"
                                  title="Supprimer définitivement"
                                >
                                  <Icon name="IconTrash" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {filteredTasks.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                          Aucune tâche trouvée avec ces filtres.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {showTaskForm && (
        <TaskForm
          task={editingTask}
          clients={clients}
          collaborators={assignees}
          onClose={() => setShowTaskForm(false)}
          onSuccess={() => {
            setShowTaskForm(false);
            loadData();
          }}
        />
      )}

      {showExportModal && (
        <ExportModal
          columns={taskColumns}
          data={filteredTasks}
          title="Liste des tâches"
          filename="taches"
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
}
